import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Clock, DollarSign, Star, Users, CheckCircle, CheckCircle2 } from "lucide-react";
import ChatBox from "@/components/ChatBox";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [counterparty, setCounterparty] = useState(null);
  const [propForm, setPropForm] = useState({
    amount: "", message: "", days_to_deliver: 7,
    experience_years: "", relevant_experience: "", portfolio_url: "",
    availability: "flexible", start_date: "", milestones: "", questions_for_client: "",
  });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const p = await api.get(`/projects/${id}`);
      setProject(p.data);
      if (user) {
        try {
          const pr = await api.get(`/projects/${id}/proposals`);
          setProposals(pr.data);
        } catch {}
      }
    } catch { toast.error("Proyecto no encontrado"); navigate("/projects"); }
  };

  useEffect(() => { load(); }, [id, user]);

  const isOwner = user && project && project.owner_id === user.user_id;
  const myProposal = user && proposals.find(p => p.freelancer_id === user.user_id);
  const isAcceptedFreelancer = user && project && project.accepted_freelancer_id === user.user_id;
  // Chat rules mirror backend: after assignment only owner + accepted freelancer.
  const projectAssigned = project && (project.status === "in_progress" || project.status === "completed");
  const canChat = user && project && (
    projectAssigned ? (isOwner || isAcceptedFreelancer)
                    : (isOwner || !!myProposal)
  );

  // Fetch counterparty info when project is assigned
  useEffect(() => {
    if (!user || !project || !projectAssigned) { setCounterparty(null); return; }
    const targetId = isOwner ? project.accepted_freelancer_id : project.owner_id;
    if (!targetId) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${targetId}`);
        setCounterparty(data);
      } catch { setCounterparty(null); }
    })();
  }, [user, project?.project_id, project?.accepted_freelancer_id, project?.status, isOwner]);

  const submitProposal = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login"); return; }
    setSubmitting(true);
    try {
      await api.post(`/projects/${id}/proposals`, {
        amount: parseFloat(propForm.amount),
        message: propForm.message,
        days_to_deliver: parseInt(propForm.days_to_deliver, 10),
        experience_years: propForm.experience_years ? parseInt(propForm.experience_years, 10) : null,
        relevant_experience: propForm.relevant_experience,
        portfolio_url: propForm.portfolio_url,
        availability: propForm.availability,
        start_date: propForm.start_date,
        milestones: propForm.milestones,
        questions_for_client: propForm.questions_for_client,
      });
      toast.success("Propuesta enviada");
      setDialogOpen(false);
      setPropForm({
        amount: "", message: "", days_to_deliver: 7,
        experience_years: "", relevant_experience: "", portfolio_url: "",
        availability: "flexible", start_date: "", milestones: "", questions_for_client: "",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const acceptProposal = async (proposalId) => {
    try {
      await api.post(`/proposals/${proposalId}/accept`);
      toast.success("Propuesta aceptada. Ya puedes chatear con el freelancer.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aceptar");
    }
  };

  const complete = async () => {
    try {
      await api.post(`/projects/${id}/complete`);
      toast.success("Proyecto marcado como completado");
      load();
    } catch (err) { toast.error("Error"); }
  };

  const submitReview = async () => {
    const targetId = isOwner ? project.accepted_freelancer_id : project.owner_id;
    try {
      await api.post("/reviews", {
        project_id: id,
        to_user_id: targetId,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success("Reseña enviada");
      loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const loadReviews = async () => {
    if (!project) return;
    try {
      const targetId = isOwner ? project.accepted_freelancer_id : project.owner_id;
      if (targetId) {
        const { data } = await api.get(`/users/${targetId}/reviews`);
        setReviews(data.filter(r => r.project_id === id));
      }
    } catch {}
  };

  useEffect(() => { if (project) loadReviews(); }, [project?.project_id, project?.accepted_freelancer_id]);

  if (!project) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Badge variant="secondary" className="uppercase text-[10px] tracking-widest font-semibold rounded-sm mb-3">
                {project.category}
              </Badge>
              <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight" data-testid="project-title">{project.title}</h1>
              <p className="text-muted-foreground mt-2">
                {project.company_name ? (
                  <>Publicado por <span className="text-foreground font-medium">{project.company_name}</span> · {project.owner_name}</>
                ) : (
                  <>Publicado por <span className="text-foreground font-medium">{project.owner_name}</span></>
                )}
              </p>
            </div>
            <span className={`text-[11px] tracking-widest uppercase font-bold px-3 py-1.5 rounded-sm ${
              project.status === 'open' ? 'bg-primary/10 text-primary' :
              project.status === 'in_progress' ? 'bg-amber-500/10 text-amber-600' :
              'bg-emerald-500/10 text-emerald-600'
            }`} data-testid="project-status-badge">
              {project.status === 'open' ? 'Abierto' : project.status === 'in_progress' ? 'En curso' : 'Completado'}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <Card>
              <CardContent className="p-8">
                <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground mb-4">Descripción</div>
                <div className="text-base leading-relaxed space-y-4">
                  {(project.description || "").split(/\n\n+/).map((block, i) => {
                    const m = block.match(/^\*\*(.+?)\*\*\s*\n?([\s\S]*)$/);
                    if (m) {
                      return (
                        <div key={i}>
                          <div className="text-xs tracking-widest uppercase font-bold text-primary mb-2">{m[1]}</div>
                          <div className="whitespace-pre-line">{m[2]}</div>
                        </div>
                      );
                    }
                    return <div key={i} className="whitespace-pre-line">{block}</div>;
                  })}
                </div>
                {project.skills?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="text-xs tracking-widest uppercase font-bold text-muted-foreground mb-3">Habilidades</div>
                    <div className="flex flex-wrap gap-2">
                      {project.skills.map(s => (
                        <span key={s} className="text-sm px-3 py-1 rounded-full bg-accent text-accent-foreground">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Proposals list for owner */}
            {isOwner && (
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight mb-4">Propuestas ({proposals.length})</h2>
                {proposals.length === 0 ? (
                  <div className="border border-dashed border-border rounded-lg p-8 text-center text-muted-foreground">
                    Aún no hay propuestas.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {proposals.map((p) => (
                      <Card key={p.proposal_id} data-testid={`proposal-${p.proposal_id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={p.freelancer_picture} />
                                <AvatarFallback>{p.freelancer_name?.[0] || 'F'}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium flex items-center gap-2">
                                  {p.freelancer_name}
                                  {p.freelancer_avg_rating > 0 && (
                                    <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                                      <Star className="w-3 h-3 fill-primary text-primary" />
                                      <span className="font-semibold text-foreground">{p.freelancer_avg_rating.toFixed(1)}</span>
                                      <span className="text-[10px]">({p.freelancer_reviews_count})</span>
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {p.days_to_deliver} días · {p.experience_years ? `${p.experience_years} años exp.` : 'Sin exp. declarada'}
                                  {p.availability && p.availability !== 'flexible' ? ` · ${{
                                    full_time: 'Tiempo completo', part_time: 'Medio tiempo', weekends: 'Fines de semana'
                                  }[p.availability] || p.availability}` : ''}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-heading font-bold tracking-tight">${p.amount.toFixed(0)}</div>
                              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p.status}</div>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{p.message}</p>

                          {(p.relevant_experience || p.portfolio_url || p.milestones || p.questions_for_client || p.start_date) && (
                            <div className="mt-4 pt-4 border-t border-border space-y-3">
                              {p.relevant_experience && (
                                <div>
                                  <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-1">Experiencia relevante</div>
                                  <p className="text-sm">{p.relevant_experience}</p>
                                </div>
                              )}
                              {p.milestones && (
                                <div>
                                  <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground mb-1">Plan de trabajo</div>
                                  <p className="text-sm whitespace-pre-line">{p.milestones}</p>
                                </div>
                              )}
                              <div className="flex flex-wrap gap-4">
                                {p.start_date && (
                                  <div>
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">Puede empezar</div>
                                    <div className="text-sm font-medium">{p.start_date}</div>
                                  </div>
                                )}
                                {p.portfolio_url && (
                                  <div>
                                    <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">Portafolio</div>
                                    <a href={p.portfolio_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">
                                      Ver →
                                    </a>
                                  </div>
                                )}
                              </div>
                              {p.questions_for_client && (
                                <div>
                                  <div className="text-[10px] tracking-widest uppercase font-bold text-primary mb-1">Preguntas al cliente</div>
                                  <p className="text-sm bg-accent/50 p-3 rounded-md">{p.questions_for_client}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {p.status === "pending" && project.status === "open" && (
                            <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button className="rounded-full" data-testid={`accept-proposal-${p.proposal_id}`}>
                                    <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                    Aceptar propuesta
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>¿Aceptar la propuesta de {p.freelancer_name}?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      El proyecto pasará a "En curso" y podrás chatear con {p.freelancer_name} para coordinar la entrega.
                                      El pago acordado (${p.amount?.toFixed(0)}) se maneja fuera de la plataforma;
                                      cuando el trabajo esté listo, marca el proyecto como completado.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => acceptProposal(p.proposal_id)}
                                      data-testid={`accept-proposal-confirm-${p.proposal_id}`}
                                    >
                                      Aceptar
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My proposal for freelancer */}
            {!isOwner && myProposal && (
              <Card>
                <CardContent className="p-6">
                  <div className="text-xs tracking-widest uppercase font-bold text-primary mb-3">Tu propuesta</div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-3xl font-heading font-bold">${myProposal.amount.toFixed(0)}</div>
                    <span className="text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-accent text-accent-foreground">
                      {myProposal.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{myProposal.message}</p>
                </CardContent>
              </Card>
            )}

            {/* Chat */}
            {canChat && projectAssigned && (
              <div>
                <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
                  <h2 className="font-heading text-2xl font-semibold tracking-tight">Conversación</h2>
                  <span className="text-[10px] tracking-widest uppercase font-bold text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-sm">
                    Proyecto asignado
                  </span>
                </div>
                {counterparty && (
                  <Card className="mb-4">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Avatar className="w-11 h-11">
                        <AvatarImage src={counterparty.picture} />
                        <AvatarFallback>{(counterparty.name || 'U')[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">
                          {isOwner ? "Freelancer asignado" : "Empresa cliente"}
                        </div>
                        <div className="font-medium truncate">{counterparty.name}</div>
                      </div>
                      {counterparty.avg_rating > 0 && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Star className="w-3.5 h-3.5 fill-primary text-primary" />
                          {counterparty.avg_rating.toFixed(1)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                <ChatBox projectId={project.project_id} currentUserId={user.user_id} />
              </div>
            )}

            {/* Reviews */}
            {project.status === "completed" && (
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight mb-4">Reseñas</h2>
                {(isOwner || isAcceptedFreelancer) && reviews.find(r => r.from_user_id === user.user_id) == null && (
                  <Card className="mb-4">
                    <CardContent className="p-6">
                      <div className="text-xs tracking-widest uppercase font-bold mb-3">Deja tu reseña</div>
                      <div className="flex gap-1 mb-3">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`star-${n}`}>
                            <Star className={`w-6 h-6 ${n <= reviewForm.rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />
                          </button>
                        ))}
                      </div>
                      <Textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Comparte tu experiencia…" data-testid="review-comment" />
                      <Button onClick={submitReview} className="rounded-full mt-3" data-testid="submit-review-btn">Enviar reseña</Button>
                    </CardContent>
                  </Card>
                )}
                {reviews.map(r => (
                  <Card key={r.review_id} className="mb-3">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />)}
                        </div>
                        <span className="text-sm font-medium">{r.from_user_name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{r.comment}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <div className="text-xs tracking-widest uppercase text-muted-foreground mb-1 font-bold">Presupuesto</div>
                  <div className="text-3xl font-heading font-bold tracking-tight flex items-center gap-1"><DollarSign className="w-6 h-6" />{project.budget?.toFixed(0)}</div>
                </div>
                <div className="pt-4 border-t border-border grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold mb-1">Plazo</div>
                    <div className="text-sm flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{project.deadline_days} días</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold mb-1">Propuestas</div>
                    <div className="text-sm flex items-center gap-1"><Users className="w-3.5 h-3.5" />{project.proposals_count || 0}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Action */}
            {!isOwner && project.status === "open" && !myProposal && user && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full rounded-full h-11" data-testid="submit-proposal-open-btn">
                    Enviar propuesta
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Enviar propuesta</DialogTitle>
                    <DialogDescription>Comparte los detalles para que el cliente pueda evaluar tu candidatura.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={submitProposal} className="space-y-5">
                    {/* Monto y plazo */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">Monto (USD) *</Label>
                        <Input type="number" min="10" required value={propForm.amount} onChange={(e) => setPropForm({ ...propForm, amount: e.target.value })}
                          className="mt-1.5 h-11" data-testid="proposal-amount" />
                      </div>
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">Días de entrega *</Label>
                        <Input type="number" min="1" required value={propForm.days_to_deliver} onChange={(e) => setPropForm({ ...propForm, days_to_deliver: e.target.value })}
                          className="mt-1.5 h-11" data-testid="proposal-days" />
                      </div>
                    </div>

                    {/* Carta de presentación */}
                    <div>
                      <Label className="text-xs tracking-widest uppercase font-semibold">Carta de presentación *</Label>
                      <Textarea required rows={4} value={propForm.message} onChange={(e) => setPropForm({ ...propForm, message: e.target.value })}
                        placeholder="Preséntate y explica por qué eres la persona ideal para este proyecto…" className="mt-1.5" data-testid="proposal-message" />
                    </div>

                    {/* Experiencia */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">Años de experiencia</Label>
                        <Input type="number" min="0" max="60" value={propForm.experience_years} onChange={(e) => setPropForm({ ...propForm, experience_years: e.target.value })}
                          placeholder="Ej. 5" className="mt-1.5 h-11" data-testid="proposal-experience-years" />
                      </div>
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">Portafolio (URL)</Label>
                        <Input type="url" value={propForm.portfolio_url} onChange={(e) => setPropForm({ ...propForm, portfolio_url: e.target.value })}
                          placeholder="https://tuportafolio.com" className="mt-1.5 h-11" data-testid="proposal-portfolio" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs tracking-widest uppercase font-semibold">Experiencia relevante</Label>
                      <Textarea rows={3} value={propForm.relevant_experience} onChange={(e) => setPropForm({ ...propForm, relevant_experience: e.target.value })}
                        placeholder="Describe 1–3 proyectos similares que hayas completado…" className="mt-1.5" data-testid="proposal-relevant-experience" />
                    </div>

                    {/* Disponibilidad + fecha de inicio */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">Disponibilidad</Label>
                        <Select value={propForm.availability} onValueChange={(v) => setPropForm({ ...propForm, availability: v })}>
                          <SelectTrigger className="mt-1.5 h-11" data-testid="proposal-availability"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full_time">Tiempo completo</SelectItem>
                            <SelectItem value="part_time">Medio tiempo</SelectItem>
                            <SelectItem value="weekends">Fines de semana</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs tracking-widest uppercase font-semibold">¿Cuándo puedes empezar?</Label>
                        <Input type="date" value={propForm.start_date} onChange={(e) => setPropForm({ ...propForm, start_date: e.target.value })}
                          className="mt-1.5 h-11" data-testid="proposal-start-date" />
                      </div>
                    </div>

                    {/* Plan de hitos */}
                    <div>
                      <Label className="text-xs tracking-widest uppercase font-semibold">Plan de trabajo / hitos</Label>
                      <Textarea rows={3} value={propForm.milestones} onChange={(e) => setPropForm({ ...propForm, milestones: e.target.value })}
                        placeholder="Ej. Día 1–2: research; Día 3–4: prototipo; Día 5: revisión y entrega" className="mt-1.5" data-testid="proposal-milestones" />
                    </div>

                    {/* Preguntas al cliente */}
                    <div>
                      <Label className="text-xs tracking-widest uppercase font-semibold">Preguntas al cliente (opcional)</Label>
                      <Textarea rows={2} value={propForm.questions_for_client} onChange={(e) => setPropForm({ ...propForm, questions_for_client: e.target.value })}
                        placeholder="¿Algo que necesites aclarar antes de arrancar?" className="mt-1.5" data-testid="proposal-questions" />
                    </div>

                    <Button type="submit" disabled={submitting} className="rounded-full w-full h-11" data-testid="submit-proposal-btn">
                      {submitting ? "Enviando…" : "Enviar propuesta"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {!user && (
              <Link to="/login">
                <Button className="w-full rounded-full h-11" data-testid="login-to-bid-btn">Inicia sesión para postularte</Button>
              </Link>
            )}

            {isOwner && project.status === "in_progress" && (
              <Button onClick={complete} className="w-full rounded-full h-11" data-testid="complete-project-btn">
                <CheckCircle className="w-4 h-4 mr-2" /> Marcar como completado
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
